import React, {useState} from 'react';
import {createRoot} from 'react-dom/client';
import Landmark from '@core/scripts/components/landmark-adapter';
import MessageBroker from 'deprecated-shared-ui/components/message-broker';
import CheckoutMessages from '../../components/message-list';
import openLayer from '@core/scripts/components/layers/open-layer';
import CheckoutErrorLayer from '@core/scripts/components/layers/CheckoutErrorLayer.jsx';
import {Alert} from 'bonprix-design-system';
import JshModule from '@core/scripts/helper/jsh-module';
import Trinity from '@core/scripts/components/trinity-adapter.js';
import {trackErrorViewDoubleAccountLogin} from '@checkout/components/helper/TrinityTrackingHelper.js';
import {startVirtualPageView} from '@core/scripts/components/tracking/trinity/virtual-page-view';
import {LOGIN_GUEST_TOGGLE_EVENT, loginOrigin, loginPageEvent} from '@core/scripts/components/tracking/trinity/login-page-events';

// Renders a login/pre-signup error (or account-deleted success) banner as a DeSy Alert.
// Stateful so the close icon works even though these are mounted imperatively via createRoot.
// markerClass keeps the `.presignup__error` hook that login.jsx queries to avoid duplicates.
function LoginAlert({status, showClose, html, markerClass}) {
    const [open, setOpen] = useState(true);
    if (!open) {
        return null;
    }
    return (
        <div className={markerClass}>
            <Alert status={status} isOpen showCloseIcon={showClose} onClose={() => setOpen(false)}>
                <span dangerouslySetInnerHTML={{__html: html}} />
            </Alert>
        </div>
    );
}

const {
    moduleScope,
    config: {loginPageConfig, errors},
} = JshModule('checkout/login');

const loginPageLandmark = loginPageConfig.loginPageLandmark;
let loginPageLandmarkSent = false;
const preSignUpPageLandmark = loginPageConfig.preSignUpPageLandmark;
const toggleItemNodes = moduleScope.querySelectorAll('[jsh-login__toggle-item]');
let preSignUpPageLandmarkSent, currentValue;
const trackingOrigin = loginOrigin(!!loginPageConfig.isCheckout, new URLSearchParams(window.location.search).get('target'));
// Whether the registration tab shows the guest variant; it decides the page view of that tab.
let guestSelected = false;

window.addEventListener(LOGIN_GUEST_TOGGLE_EVENT, (event) => {
    guestSelected = !!event.detail?.guest;
});

toggleItemNodes.forEach((node) => {
    node.addEventListener('click', onToggleItemClick);
});

MessageBroker.subscribe(CheckoutMessages.PRESIGNUP.EMAIL_ALREADY_IN_USE, ({email, message}) => {
    const preSignUpError = document.querySelector('.presignup__error');
    const pageName = 'login';

    if (!preSignUpError) {
        const loginError = (
            <LoginAlert status="error" showClose html={message} markerClass="presignup__error" />
        );

        insertLoginErrorContainer(loginError);
    }

    trackErrorViewDoubleAccountLogin(pageName, message);

    const toggleLoginNode = moduleScope.querySelector("[jsh-login__toggle-item='login'] [jsh-toggle-input]");
    toggleLoginNode.click();
    const loginEmailField = moduleScope.querySelector("[name='username']");
    loginEmailField.value = email;
    const loginPasswordField = moduleScope.querySelector("[name='password']");
    loginPasswordField.value = '';
});

const checkedToggle = moduleScope.querySelector('[jsh-toggle-input]:checked');

if (checkedToggle) {
    const tempValue = checkedToggle.parentElement.getAttribute('jsh-login__toggle-item');
    changeForm(tempValue);
    window.addEventListener('load', () => {
        dispatchViewTabEvent(tempValue);
    });
}

if (Array.isArray(errors)) {
    const allEmpty = errors?.length === 0 || errors.every((error) => error === '');
    if (!allEmpty) {
        handleErrors(errors, loginPageConfig.texts);
    }
}

function insertLoginErrorContainer(errorHint) {
    const loginErrorContainer = document.createElement('div');
    const checkoutHeader = document.querySelector('.checkout-header');
    if (checkoutHeader !== null) {
        checkoutHeader.after(loginErrorContainer);
    } else {
        const checkoutOuterGutter = document.querySelector('.checkout__outer-gutter');
        checkoutOuterGutter.before(loginErrorContainer);
    }
    const root = createRoot(loginErrorContainer);

    root.render(errorHint);
}

function onToggleItemClick(event) {
    const currentItemNode = event.target.closest('[jsh-login__toggle-item]');
    const tempValue = currentItemNode.getAttribute('jsh-login__toggle-item');

    if (tempValue !== currentValue) {
        // A tab switch is a page view of its own: the server reported only the tab the page opened with.
        startVirtualPageView(loginPageEvent(tabPageView(tempValue), trackingOrigin));
        changeForm(tempValue);
    }
}

function tabPageView(tab) {
    if (tab === 'register') {
        return guestSelected ? 'guest' : 'register';
    }
    return 'login';
}

function changeForm(value) {
    if (value === 'register') {
        moduleScope.querySelector('[jsh-login]').classList.add('hidden');
        moduleScope.querySelector('[jsh-signup]').classList.remove('hidden');

        if (!preSignUpPageLandmarkSent) {
            Landmark.sendPageLandmark(preSignUpPageLandmark);
            preSignUpPageLandmarkSent = true;
        }
    } else {
        moduleScope.querySelector('[jsh-signup]').classList.add('hidden');
        moduleScope.querySelector('[jsh-login]').classList.remove('hidden');

        if (!loginPageLandmarkSent) {
            Landmark.sendPageLandmark(loginPageLandmark);
            loginPageLandmarkSent = true;
        }
    }
    currentValue = value;
    dispatchViewTabEvent(currentValue);
}

function handleErrors(errors, texts) {
    const allUnknownError = errors.every((error) => !texts.hasOwnProperty(error));
    const showLayer = errors.find((error) => error === 'login.technical.error') || allUnknownError;
    const accountSuccessfullyDeleted = errors.find((error) => error === 'login.account.successfully.deleted');

    if (showLayer) {
        return openLayer(CheckoutErrorLayer, {texts: texts});
    }

    const errorHints = [];
    errors.map((error, index) =>
        errorHints.push(
            <LoginAlert
                key={index}
                status={accountSuccessfullyDeleted ? 'success' : 'error'}
                showClose={!accountSuccessfullyDeleted}
                html={texts[error]}
            />,
        ),
    );

    insertLoginErrorContainer(errorHints);
}

// We have a handlebar page with react components.
// The Login page always renders the register and login tab with all their React components and hides the components
// from the not-active tab. Therefor the React components do not know if they are in view or not. With this event, we
// send an event to inform which tab is displayed and anyone who needs can listen to it and execute any action based on it.
function dispatchViewTabEvent(currentTab) {
    window.dispatchEvent(new CustomEvent('viewTab', {detail: {activeTab: currentTab}}));
}
